// Formatting helpers for fitness metrics.

import { formatActivityDate, formatActivityDateTime, parseActivityInstant } from "./dateTime";

export const METERS_PER_MILE = 1609.34;
export const KG_PER_LB = 0.453592;

export const distanceUnit = (unitSystem = "metric") =>
  unitSystem === "imperial" ? "mi" : "km";

export const speedUnit = (sport = "", unitSystem = "metric") => {
  const isRun = /run|walk/i.test(sport);
  if (isRun) return unitSystem === "imperial" ? "/mi" : "/km";
  return unitSystem === "imperial" ? "mph" : "km/h";
};

export const distanceValue = (meters, unitSystem = "metric") => {
  if (meters == null) return null;
  return unitSystem === "imperial" ? meters / METERS_PER_MILE : meters / 1000;
};

export const speedValue = (ms, unitSystem = "metric") => {
  if (ms == null) return null;
  return unitSystem === "imperial" ? ms * 2.23694 : ms * 3.6;
};

export const weightValue = (kg, unitSystem = "metric") => {
  if (kg == null) return null;
  return unitSystem === "imperial" ? kg / KG_PER_LB : kg;
};

export const toKg = (value, unitSystem = "metric") => {
  if (value == null) return null;
  return unitSystem === "imperial" ? value * KG_PER_LB : value;
};

export const fmtDistance = (meters, unitSystem = "metric", digits = 2) => {
  const value = distanceValue(meters, unitSystem);
  if (value == null) return "—";
  return `${value.toFixed(digits)} ${distanceUnit(unitSystem)}`;
};

export const fmtDuration = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

/** Compact duration for timeline summaries, e.g. "8h 9m". */
export const fmtDurationShort = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

export const fmtElevation = (meters) => {
  if (meters == null) return "—";
  return `${Math.round(meters).toLocaleString()} m`;
};

export const fmtSpeed = (ms, sport = "", unitSystem = "metric") => {
  if (ms == null) return "—";
  const isRun = /run|walk/i.test(sport);
  if (isRun) {
    // pace min/km
    if (ms < 0.1) return "—";
    const metersPerUnit = unitSystem === "imperial" ? METERS_PER_MILE : 1000;
    const paceSec = metersPerUnit / ms;
    const m = Math.floor(paceSec / 60);
    const s = Math.round(paceSec % 60);
    return `${m}:${String(s).padStart(2, "0")} ${speedUnit(sport, unitSystem)}`;
  }
  const speed = speedValue(ms, unitSystem);
  return `${speed.toFixed(1)} ${speedUnit(sport, unitSystem)}`;
};

export const fmtWeight = (kg, unitSystem = "metric", digits = 1) => {
  const value = weightValue(kg, unitSystem);
  if (value == null) return "—";
  const unit = unitSystem === "imperial" ? "lb" : "kg";
  return `${value.toFixed(digits)} ${unit}`;
};

export const fmtNum = (v, digits = 0, unit = "") => {
  if (v == null) return "—";
  return `${Number(v).toFixed(digits)}${unit ? " " + unit : ""}`;
};

export const fmtDate = (isoOrActivity) => {
  if (!isoOrActivity) return "—";
  if (typeof isoOrActivity === "object") {
    return formatActivityDate(isoOrActivity);
  }
  try {
    const parsed = parseActivityInstant(isoOrActivity);
    if (!parsed) return "—";
    return parsed.format("MMM D, YYYY");
  } catch {
    return "—";
  }
};

export const fmtDateTime = (isoOrActivity) => {
  if (!isoOrActivity) return "—";
  if (typeof isoOrActivity === "object") {
    return formatActivityDateTime(isoOrActivity);
  }
  try {
    const parsed = parseActivityInstant(isoOrActivity);
    if (!parsed) return "—";
    return parsed.format("MMM D, YYYY h:mm A");
  } catch {
    return "—";
  }
};

export const sportIcon = {
  cycling: "Bike",
  running: "Footprints",
  run: "Footprints",
  walking: "Footprints",
  hiking: "Mountain",
  swimming: "Waves",
  workout: "Activity",
  other: "Activity",
};
